import { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Pagination from "@material-ui/lab/Pagination";
import { useGetTransferHistory, useExportTransferHistoryPdf } from "../../../../utils/react-query/queryInventory/queries";
import CustomButton from "../../../../components/Button/CustomButton";
import type { InventoryTableRow, TransferHistoryResponse } from "../ProductsTable/types";

interface TransferHistoryModalProps {
  open: boolean;
  product: InventoryTableRow;
  onClose: () => void;
}

const ROWS_PER_PAGE = 10;

/**
 * Modal that displays the transfer audit log for a single product.
 * Shows: date, from warehouse, to warehouse, quantity, and who performed it.
 * Supports search filtering and server-side pagination.
 */
export function TransferHistoryModal({
  open,
  product,
  onClose,
}: TransferHistoryModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const { mutate: exportPdf, isLoading: isExporting } = useExportTransferHistoryPdf();

  // Reset state when modal opens for a different product
  useEffect(() => {
    if (open) {
      setPage(1);
      setSearchQuery("");
    }
  }, [open, product.id]);

  const { data, isLoading } = useGetTransferHistory(
    product.id,
    page,
    ROWS_PER_PAGE
  );

  // Normalize response: always treat as paginated envelope
  const response: TransferHistoryResponse = useMemo(() => {
    if (!data) {
      return { results: [], total: 0, page: 1, totalPages: 0 };
    }
    // Backward compatible: if data is a flat array (no page/limit sent)
    if (Array.isArray(data)) {
      return { results: data, total: data.length, page: 1, totalPages: 1 };
    }
    return data;
  }, [data]);

  const results = response.results;

  // Client-side filter across name fields
  const filteredResults = useMemo(() => {
    if (!searchQuery.trim()) return results;
    const query = searchQuery.toLowerCase();
    return results.filter((entry) =>
      [
        entry.product_name,
        entry.from_address_name,
        entry.to_address_name,
        entry.performed_by_name,
      ]
        .filter(Boolean)
        .some((field) => field!.toLowerCase().includes(query))
    );
  }, [results, searchQuery]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString("es-CO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle disableTypography style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6">
          Historial de traslados — {product.name}
        </Typography>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <CustomButton
            color="outlined"
            size="small"
            onClick={() => exportPdf()}
            disabled={isExporting}
          >
            {isExporting ? (
              // CustomButton resets any inner CircularProgress to an absolute
              // center (its own `isLoading` layout), so the inline spinner
              // restores the flow position before the label while exporting.
              <CircularProgress
                size={16}
                style={{ marginRight: 8, position: "static", transform: "none" }}
              />
            ) : null}
            Exportar PDF
          </CustomButton>
          <IconButton size="small" onClick={onClose} aria-label="cerrar">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "2rem" }}>
            <CircularProgress size={32} />
          </div>
        )}

        {!isLoading && results.length === 0 && (
          <Typography
            variant="body2"
            color="textSecondary"
            align="center"
            style={{ padding: "2rem" }}
          >
            No hay traslados registrados para este producto.
          </Typography>
        )}

        {!isLoading && results.length > 0 && (
          <>
            <Box mb={2}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Buscar por origen, destino o realizado por..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>

            {filteredResults.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                align="center"
                style={{ padding: "2rem" }}
              >
                No se encontraron traslados con ese criterio de busqueda.
              </Typography>
            ) : (
              <>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Fecha</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Origen</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Destino</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Cantidad</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Realizado por</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredResults.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell style={{ whiteSpace: "nowrap" }}>
                          {formatDate(entry.created_at)}
                        </TableCell>
                        <TableCell>{entry.from_address_name}</TableCell>
                        <TableCell>{entry.to_address_name}</TableCell>
                        <TableCell align="right">{entry.quantity}</TableCell>
                        <TableCell>
                          {entry.performed_by_name || (
                            <Typography
                              variant="body2"
                              component="span"
                              color="textSecondary"
                            >
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {response.totalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={response.totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      size="small"
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default TransferHistoryModal;
